import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type PagedResponse, type Response } from '@/lib/api'
import { showError, showInfo, showSuccess, showWarning } from '@/lib/alert'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useAuth } from '@/auth/AuthContext'
import { Preloader } from '@/components/Preloader'
import { DataNotFound } from '@/components/DataNotFound'
import { Pagination } from '@/components/Pagination'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  careerOptions,
  companyOptions,
  positionOptions,
  useCareers,
  useCompanies,
  usePositions,
} from '@/features/masterData/queries'
import { VacancyCard } from '@/features/vacancy/VacancyCard'
import type { VacancyRow } from '@/features/vacancy/types'
import './vacancy.css'

/**
 * Port of Views/Vacancy/Index.cshtml + IndexBeforeLogin.cshtml and their two
 * scripts (vacancy.js, vacancyBeforeLogin.js).
 *
 * VacancyController.Index picked between the two views, and could redirect a
 * signed-in user with an incomplete profile to the profile form. That decision
 * still belongs to the server, so it comes from GET vacancy/page-context; this
 * component renders whichever variant the answer names.
 *
 * The two views were identical apart from three things, all driven by `signedIn`
 * here: the AI-match toggle (commented out in the anonymous view), the bookmark
 * button on each card, and the talent-community button (a link to /Account/Login
 * when signed out).
 */
interface PageContext {
  isAuthenticated: boolean
  isProfileCompleted: boolean
  view?: 'Index' | 'IndexBeforeLogin'
  redirectTo?: string
  message?: string
  messageType?: string
  param: { mstCompanyID: number; mstPositionID: number; mstCareerID: number; isActive: boolean }
  userEmail?: string
}

const RECORD_OPTIONS = [15, 30, 45, 60]

export function VacancyIndex(): JSX.Element {
  usePageTitle('Vacancy')
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const [company, setCompany] = useState('')
  const [position, setPosition] = useState('')
  const [career, setCareer] = useState('')
  /** Applied filters — the hidden fields the original searched on. */
  const [applied, setApplied] = useState({ company: 0, position: 0, career: 0 })
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(15)
  const [aiMatch, setAiMatch] = useState(false)
  const [isMatching, setIsMatching] = useState(false)

  const careers = useCareers()
  const companies = useCompanies()
  const positions = usePositions()

  const context = useQuery({
    queryKey: ['vacancy', 'page-context'],
    queryFn: () => api.get<PageContext>('/api/ecareer/vacancy/page-context'),
    // Read-once on the server (it consumes the stored filters), so never refetch.
    staleTime: Infinity,
    retry: false,
  })

  // Seed the filter chips from the search the user ran on the Home page.
  useEffect(() => {
    const param = context.data?.param
    if (!param) return
    setApplied({
      company: param.mstCompanyID,
      position: param.mstPositionID,
      career: param.mstCareerID,
    })
    if (param.mstCompanyID) setCompany(String(param.mstCompanyID))
    if (param.mstPositionID) setPosition(String(param.mstPositionID))
    if (param.mstCareerID) setCareer(String(param.mstCareerID))
  }, [context.data])

  // "Silakan lengkapi profil Anda terlebih dahulu..." then off to the form.
  useEffect(() => {
    const redirectTo = context.data?.redirectTo
    if (!redirectTo) return
    void (async () => {
      if (context.data?.message) await showWarning(context.data.message)
      navigate(redirectTo)
    })()
  }, [context.data, navigate])

  const signedIn = context.data?.isAuthenticated ?? isAuthenticated

  const listing = useQuery({
    queryKey: ['vacancy', 'list', applied, pageNumber, pageSize, aiMatch, signedIn],
    queryFn: () =>
      api.post<PagedResponse<VacancyRow>>(
        signedIn ? '/api/ecareer/vacancy/list' : '/api/ecareer/vacancy/listbeforelogin',
        {
          mstCompanyID: applied.company,
          mstCareerID: applied.career,
          mstPositionID: applied.position,
          PageNumber: pageNumber,
          PageSize: pageSize,
          isActive: true,
          // Only sent when the toggle is on; 60 is the cut-off the original used.
          IsAIMatched: aiMatch,
          Score: aiMatch ? 60 : null,
        },
      ),
    enabled: !context.isLoading && !context.data?.redirectTo,
  })

  const rows = listing.data?.Data ?? []

  /** Which of the listed vacancies this candidate has bookmarked. */
  const saved = useQuery({
    queryKey: ['vacancy', 'checksaved', rows.map((row) => row.trxVacancyID)],
    queryFn: () =>
      api.post<Response<number[]>>('/api/ecareer/vacancy/checksaved', {
        vacancyIds: rows.map((row) => row.trxVacancyID),
      }),
    enabled: signedIn && rows.length > 0,
  })
  /**
   * `Data` is normalised to an array by the BFF, but this stays defensive: the
   * upstream's own `checksaved` answers with a single OBJECT, and feeding that
   * to `new Set()` threw "object is not iterable" and took the whole page down.
   * A bookmark icon is not worth crashing a listing over.
   */
  const savedIds = new Set(Array.isArray(saved.data?.Data) ? saved.data.Data : [])

  const talent = useQuery({
    queryKey: ['vacancy', 'joinedTalent'],
    queryFn: () => api.post<Response<boolean>>('/api/ecareer/vacancy/joinedTalent'),
    enabled: signedIn,
  })
  const isSubscribed = talent.data?.Data === true

  function requireLogin(message: string): void {
    void (async () => {
      await showWarning(message)
      navigate(`/Account/Login?ReturnUrl=${encodeURIComponent('/Vacancy')}`)
    })()
  }

  async function toggleSave(vacancy: VacancyRow): Promise<void> {
    if (!signedIn) {
      requireLogin('Please login first to save vacancy')
      return
    }

    const wasSaved = savedIds.has(vacancy.trxVacancyID)
    try {
      if (wasSaved) {
        await api.del('/api/ecareer/vacancy/unsavedVacancy', { vacancyId: vacancy.trxVacancyID })
        await saved.refetch()
        await showSuccess('Vacancy removed from saved list')
      } else {
        await api.post('/api/ecareer/vacancy/savedVacancy', { vacancyId: vacancy.trxVacancyID })
        await saved.refetch()
        await showSuccess('Vacancy saved successfully')
      }
    } catch (error) {
      await showError(error instanceof Error ? error.message : 'Failed to update saved vacancy')
    }
  }

  /**
   * `Vacancy.HandleAIMatchToggle` + `GetAIMatchedVacancies`: turning the switch
   * on first runs the scoring pass, then re-queries with the Score filter.
   */
  async function toggleAiMatch(enabled: boolean): Promise<void> {
    if (!signedIn) {
      requireLogin('Please login first to use AI matching')
      return
    }

    setPageNumber(1)
    if (!enabled) {
      setAiMatch(false)
      return
    }

    setIsMatching(true)
    try {
      const response = await api.post<{ matchResults: unknown[] }>(
        '/api/ecareer/gemini/vacancyMatchScore',
      )
      setAiMatch(true)
      if (response.matchResults.length > 0) {
        await showSuccess('AI matching completed successfully!')
      } else {
        await showInfo('AI matching completed, but no jobs match your profile above 60%.')
      }
    } catch (error) {
      setAiMatch(false)
      await showError(error instanceof Error ? error.message : 'Error getting AI match score')
    } finally {
      setIsMatching(false)
    }
  }

  async function toggleTalentCommunity(): Promise<void> {
    try {
      const response = await api.post<Response<unknown>>('/api/ecareer/vacancy/talentCommunity')
      if (!response.Success) {
        await showError(response.Message ?? 'Failed to update talent community status')
        return
      }
      await talent.refetch()
      await showSuccess(
        isSubscribed
          ? 'Successfully unsubscribed from talent community!'
          : 'Successfully joined our talent community!',
      )
    } catch (error) {
      await showError(
        error instanceof Error ? error.message : 'Error updating talent community status',
      )
    }
  }

  if (context.isLoading) return <Preloader />

  return (
    <>
      <section className="bg-white pt-0 pb-0">
        <div className="container">
          <div className="row justify-content-center text-center">
            {/* min-height lives in vacancy.css so it can be dropped on mobile,
                where forcing it stretched the image. */}
            <img src="/lib/images/landing/10.png" className="vacancy-hero" alt="" />
          </div>
        </div>
      </section>

      <section className="bg-white pt-0 pb-5">
        <div className="container">
          <div className="row justify-content-center button">
            <div className="col-11 col-sm-11 col-xl-11">
              <div className="card contact-card-form user-card" style={{ marginTop: -50 }}>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="row">
                        <div className="col-md-4 mb-2 p-1">
                          <SearchableSelect
                            id="slscompany"
                            options={companyOptions(companies.data?.Data)}
                            value={company}
                            onChange={setCompany}
                            placeholder="Choose Company"
                          />
                        </div>
                        <div className="col-md-4 mb-2 p-1">
                          <SearchableSelect
                            id="slsposition"
                            options={positionOptions(positions.data?.Data)}
                            value={position}
                            onChange={setPosition}
                            placeholder="Choose Position"
                          />
                        </div>
                        <div className="col-md-3 mb-2 p-1">
                          <SearchableSelect
                            id="slscareer"
                            options={careerOptions(careers.data?.Data)}
                            value={career}
                            onChange={setCareer}
                            placeholder="Choose Career"
                          />
                        </div>
                        <div className="col-md-1 mb-2 p-1 text-center">
                          <button
                            type="button"
                            className="btn btn-primary d-inline-flex"
                            id="btnSearch"
                            onClick={() => {
                              setPageNumber(1)
                              setApplied({
                                company: company === '' ? 0 : Number(company),
                                position: position === '' ? 0 : Number(position),
                                career: career === '' ? 0 : Number(career),
                              })
                            }}
                          >
                            <i className="ti ti-search me-1" />
                            Search
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white pt-0">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-4 col-md-2 mb-3">
              <select
                className="form-control"
                id="slsrecord"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value))
                  setPageNumber(1)
                }}
              >
                {RECORD_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-7 col-8">
              <h5>Records</h5>
            </div>

            {/* Commented out in IndexBeforeLogin.cshtml, so signed-in only. */}
            {signedIn && (
              <div className="row mt-3">
                <div
                  className="col-12 d-flex justify-content-start align-items-center"
                  id="aiMatchContainer"
                >
                  <div className="d-flex align-items-center">
                    <label
                      className="form-check-label me-2 d-flex align-items-center"
                      htmlFor="aiMatchToggle"
                    >
                      <i className="ti ti-robot text-primary me-1" />
                      <span className="text-muted">Show AI Matched Vacancies</span>
                    </label>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="aiMatchToggle"
                        role="switch"
                        checked={aiMatch}
                        disabled={isMatching}
                        onChange={(event) => void toggleAiMatch(event.target.checked)}
                      />
                    </div>
                    {isMatching && (
                      <div className="ms-2" id="aiMatchLoader">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Analyzing...</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {listing.isFetching && (
            <div className="row g-4 mb-5 mt-5" id="loader">
              <div className="d-flex justify-content-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          )}

          {!listing.isFetching && rows.length === 0 && (
            <div className="row g-4" id="pnlDataNotFound">
              <DataNotFound />
            </div>
          )}

          <div className="row g-4 mb-5 mt-5" id="pnlListVacancy">
            {rows.map((vacancy) => (
              <VacancyCard
                key={vacancy.trxVacancyID}
                vacancy={vacancy}
                showSaveButton={signedIn}
                isSaved={savedIds.has(vacancy.trxVacancyID)}
                onToggleSave={(row) => void toggleSave(row)}
                showScore={signedIn}
              />
            ))}
          </div>

          <div className="row g-4">
            <Pagination
              count={listing.data?.Count ?? 0}
              pageNumber={pageNumber}
              pageSize={pageSize}
              onPageChange={setPageNumber}
            />
          </div>
        </div>
      </section>

      <section className="bg-light pt-5 pb-5 position-relative">
        <div className="container">
          <div
            className="row justify-content-center text-center position-absolute w-100 h-100"
            style={{ top: 0, left: '50%', transform: 'translateX(-50%)', zIndex: 1, opacity: 0.8 }}
          >
            <img
              src="/lib/images/backgroundTalent.jpg"
              className="img-fluid h-100"
              style={{ objectFit: 'cover' }}
              alt=""
            />
          </div>

          <div className="row justify-content-center position-relative" style={{ zIndex: 2 }}>
            <div className="col-12 col-lg-8 text-center">
              <div className="talent-community-card p-5">
                <h2 className="mb-4 fw-bold" style={{ color: '#1CB5E0' }}>
                  Join Our Talent Community
                </h2>
                <p className="mb-3 fs-6" style={{ color: '#666' }}>
                  We&apos;re always looking for talented individuals!
                </p>
                <p className="mb-4 fs-6" style={{ color: '#666' }}>
                  Even if there&apos;s no perfect fit right now, you can join our talent community.
                  <br />
                  We&apos;d love to connect with you and let you know about future opportunities.
                </p>
                <div className="d-flex justify-content-center">
                  {signedIn ? (
                    <button
                      type="button"
                      className={`btn btn-lg px-4 py-2 ${isSubscribed ? 'btn-success' : 'btn-primary'}`}
                      id="btnJoinCommunity"
                      title={
                        isSubscribed
                          ? 'Click to unsubscribe'
                          : 'Join our talent community'
                      }
                      style={
                        isSubscribed
                          ? undefined
                          : {
                              background: '#1CB5E0',
                              border: 'none',
                              borderRadius: 8,
                              color: 'white',
                            }
                      }
                      onClick={() => void toggleTalentCommunity()}
                    >
                      <i className={`ti ${isSubscribed ? 'ti-check' : 'ti-users'} me-2`} />
                      {isSubscribed ? 'Subscribed' : 'Join Now'}
                    </button>
                  ) : (
                    <a
                      href="/Account/Login"
                      className="btn btn-lg px-4 py-2"
                      style={{
                        background: '#1CB5E0',
                        border: 'none',
                        borderRadius: 8,
                        color: 'white',
                        textDecoration: 'none',
                      }}
                      onClick={(event) => {
                        event.preventDefault()
                        navigate('/Account/Login')
                      }}
                    >
                      <i className="ti ti-users me-2" />
                      Join Now
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
